import { useEffect, useState } from 'react';

const NOT_TYPED = new Set([
  'checkbox',
  'radio',
  'button',
  'submit',
  'reset',
  'file',
  'range',
  'color',
  'image',
]);

function isTypingInto(el: Element | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  if (el instanceof HTMLInputElement) return !NOT_TYPED.has(el.type);
  return (
    el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement || el.isContentEditable
  );
}

/**
 * True while a text field has focus — i.e. the on-screen keyboard is up.
 * iOS Safari lifts `position: fixed; bottom: 0` bars on top of the keyboard,
 * so the layout hides the BottomTabBar while this is true. Focus only: the
 * visual viewport resizes mid-animation and is not a reliable signal, and
 * iOS "Done" blurs the field.
 */
export function useTypingInField(): boolean {
  const [typing, setTyping] = useState(() => isTypingInto(document.activeElement));

  useEffect(() => {
    const onFocusIn = (e: FocusEvent) => {
      if (isTypingInto(e.target as Element)) setTyping(true);
    };
    // One frame later, so moving from field to field doesn't flash the bar.
    let frame = 0;
    const onFocusOut = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setTyping(isTypingInto(document.activeElement)));
    };
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
    };
  }, []);

  return typing;
}
